import type { DebtAllocation, MonthlyIncomeEntry, MonthlyLedgerEntry } from '@/types'

/**
 * v0.8.56 — row-level change set between two versions of one month.
 *
 * Household sharing put two devices on the same month. Saving the whole
 * month from one device's copy erased whatever the other device had saved
 * since this one loaded (docs/product/concurrent-edits.md §1.1). Cloud
 * saves now send only the rows — and within a row only the fields — that
 * this device actually changed.
 */
export interface AllocationChange {
  debtId: string
  /** Changed fields only. A key present with `undefined` means "clear it". */
  changes: Partial<DebtAllocation>
  /** The full row after the change, for when the row is missing remotely. */
  full: DebtAllocation
}

export interface IncomeChange {
  sourceId: string
  changes: Partial<MonthlyIncomeEntry>
  full: MonthlyIncomeEntry
}

export interface LedgerEntryPatch {
  allocations: AllocationChange[]
  addedAllocations: DebtAllocation[]
  removedDebtIds: string[]
  income: IncomeChange[]
  addedIncome: MonthlyIncomeEntry[]
  removedSourceIds: string[]
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)

function changedKeys<T extends object>(before: T, after: T): Partial<T> {
  const out: Partial<T> = {}
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]) as Set<keyof T>
  for (const k of keys) {
    if (!same(before[k], after[k])) out[k] = after[k]
  }
  return out
}

function diffRows<R extends object>(
  before: R[],
  after: R[],
  key: (r: R) => string
): { changed: { key: string; changes: Partial<R>; full: R }[]; added: R[]; removed: string[] } {
  const prev = new Map(before.map((r) => [key(r), r]))
  const next = new Map(after.map((r) => [key(r), r]))
  const changed: { key: string; changes: Partial<R>; full: R }[] = []
  const added: R[] = []
  for (const [k, row] of next) {
    const old = prev.get(k)
    if (!old) {
      added.push(row)
      continue
    }
    const changes = changedKeys(old, row)
    if (Object.keys(changes).length > 0) changed.push({ key: k, changes, full: row })
  }
  const removed = [...prev.keys()].filter((k) => !next.has(k))
  return { changed, added, removed }
}

/**
 * Row-level patch from `before` to `after`, or `null` when a month-level
 * field changed (status, notes, essentials, id, month…) — those saves are
 * explicit whole-month actions (close / reopen / start) and go through
 * `upsertLedgerEntry`.
 */
export function diffLedgerEntry(
  before: MonthlyLedgerEntry,
  after: MonthlyLedgerEntry
): LedgerEntryPatch | null {
  const strip = (e: MonthlyLedgerEntry) => ({ ...e, allocations: undefined, income: undefined })
  if (!same(strip(before), strip(after))) return null

  const alloc = diffRows(before.allocations, after.allocations, (a) => a.debtId)
  const inc = diffRows(before.income, after.income, (i) => i.sourceId)
  return {
    allocations: alloc.changed.map((c) => ({ debtId: c.key, changes: c.changes, full: c.full })),
    addedAllocations: alloc.added,
    removedDebtIds: alloc.removed,
    income: inc.changed.map((c) => ({ sourceId: c.key, changes: c.changes, full: c.full })),
    addedIncome: inc.added,
    removedSourceIds: inc.removed,
  }
}

export function isEmptyPatch(p: LedgerEntryPatch): boolean {
  return (
    p.allocations.length === 0 &&
    p.addedAllocations.length === 0 &&
    p.removedDebtIds.length === 0 &&
    p.income.length === 0 &&
    p.addedIncome.length === 0 &&
    p.removedSourceIds.length === 0
  )
}
